import { Box, SelectChangeEvent, Typography } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import BriaDropDown, { DropDownItem } from "../../../../../../components/common/BriaDropDown/BriaDropDown";
import { AspectRatio } from "../../../../../../components/common/DropDowns/StaticDropdown";
import useStaticDropdown from "../../../../../../components/common/DropDowns/useStaticDropdown";
import { APPS } from "../../../../../../constants/AppsConstants";
import { useAppStore } from "../../../../../../hooks/useStores";
import InputLayout from "../../../../../../layout/InputLayout/InputLayout";
import styles from "./TextToImageAspectRatio.module.scss";

const TextToImageAspectRatio = () => {
	const { t } = useTranslation("translation", { keyPrefix: "playground.textToImage.config" });
	const { items } = useStaticDropdown({ tKeyPrefix: "staticDropdowns.aspectRatio" });
	const { textToImageStore, playgroundStore } = useAppStore();
	const { config, handleConfigChange } = textToImageStore;
	const [localAspectRatio, setLocalAspectRatio] = useState<AspectRatio | undefined>(undefined);
	const isFiboSelected =
		textToImageStore.config.model === "fibo" ||
		(!!textToImageStore.config.tailored_model_id && textToImageStore.config.training_version === "fibo");
	const isForceOff = textToImageStore.isImageReferenceEnabled() && !isFiboSelected;
	const isDisabled = isForceOff;

	const customSelectedKey = (item: DropDownItem<AspectRatio>) => (
		<Box className={styles.aspectRatioItem}>
			<Box className={styles.aspectRatioValueContainer}>
				<Box className={styles.aspectRatioIconContainer}>
					<Box
						sx={{ aspectRatio: String(item.value).replace(":", "/") }}
						className={styles.aspectRatioIcon}
					/>
				</Box>
				<Typography className={styles.aspectRatioValue}>{String(item.value)}</Typography>
			</Box>
			<Typography className={styles.aspectRatioKey}>{String(item.key)}</Typography>
		</Box>
	);

	const customItems = items.reduce<DropDownItem<AspectRatio>[]>((acc, item) => {
		if (item.value === "original") return acc;
		acc.push({
			...item,
			customItem: customSelectedKey(item as DropDownItem<AspectRatio>),
			customSelectedKey: customSelectedKey(item as DropDownItem<AspectRatio>),
		} as DropDownItem<AspectRatio>);
		return acc;
	}, []);

	useEffect(() => {
		if (isForceOff) {
			delete textToImageStore.config.aspect_ratio;
		} else {
			const aspectRatioToUse =
				localAspectRatio ||
				(textToImageStore.config.tailored_model_id ||
				playgroundStore.selectedConfig === APPS.TG_IMAGE_GENERATION ||
				playgroundStore.selectedConfig === APPS.TG_VIDEO_EDITING
					? "1:1"
					: "4:3");
			handleConfigChange("aspect_ratio", aspectRatioToUse);
		}
	}, [isForceOff, playgroundStore.selectedConfig, textToImageStore.config.tailored_model_id]);

	const getPlaceholder = () => {
		if (isDisabled) return t("aspectRatio.disabledPlaceholder");
		return t("aspectRatio.placeholder");
	};

	const handleAspectRatioChange = (e: SelectChangeEvent<AspectRatio>) => {
		const newValue = e.target.value as AspectRatio;
		handleConfigChange("aspect_ratio", newValue);
		setLocalAspectRatio(newValue);
	};

	return (
		<InputLayout label={t("aspectRatio.label")}>
			<BriaDropDown<AspectRatio>
				items={customItems}
				value={config?.aspect_ratio}
				onChange={handleAspectRatioChange}
				placeholder={getPlaceholder()}
				disabled={isDisabled}
			/>
		</InputLayout>
	);
};

export default observer(TextToImageAspectRatio);
